import { reactive } from 'vue';
import { useStore } from '@/store';
import axios from '@axios';
import { getWssEndpoint } from '../../utils/config';
import { logEvent } from '../../utils/utils';
import { GlobalStickerSize, StickerType } from '@/utils/constants';
import { showGlobalError } from '@/utils/error';

class StickersTabShowSticker {
	// Vuex store.
	store;

	// Contains the sticker the viewer has chosen to slap.
	stickerToSlap;

	// Web socket connection for bits purchase.
	ws;

	// Callback passed into showSticker that is invoked after
	// sticker processing has completed.
	onComplete;

	// Callback passed into showSticker that is only invoked if a bits transaction has occurred.
	onBitsUsed;

	// For bits transactions, the Twitch extension helper SDK issues the onTransactionComplete callback
	// too all instances of the extension, even those on different browser tabs and between mobile and
	// desktop. This flag defines if the callback was initiated from the current instance.  Not foolproof
	// as near-concurrent clicks in separate tabs may result in undefined behaviour.
	isInTransaction;

	// Bits transaction ids that have been processed. This is used by the
	// EventSub backup process so we know when the bits transaction has occurred
	// if the JavaScript callback fails.
	transactionIds;

	constructor() {
		this.store = useStore();

		this.stickerToSlap = reactive({
			id: null,
			stickerType: null,
			name: null,
			sku: null,
			bits: 0,
			image: null,
			subOnly: false,
		});

		this.isInTransaction = false;
		this.transactionIds = [];
	}

	// Initiate the show sticker process.
	showSticker(isBroadcaster, onComplete, onBitsUsed) {
		const self = this;
		const s = this.stickerToSlap;

		const payload = {
			stickerId: s.id,
			stickerType: s.stickerType,
			stickerName: s.name,
			extensionVersion: this.store.state.extensionVersion,
			customPlacement: s.isStickerPlacementEnabled ? s.customPlacement : null,
		};

		// Set TTS text.
		if (s.stickerType == StickerType.TTS) {
			payload.tts = { text: s.tts.text };
		}

		self.onComplete = onComplete;

		// After the bits transaction succeeds, send the transaction to the server.
		// We use EventSub on the server as a backup.
		window.Twitch.ext.bits.onTransactionComplete(function (transactionObject) {
			// The Twitch helper will issue the callback to all browser instances and tabs,
			// so we need to ensure that the current user initiated the bits transaction.
			if (transactionObject.initiator != 'current_user') {
				return;
			} else if (!self.isInTransaction) {
				//console.log('Extension instance not in transaction, exiting sticker slap processing.');
				return;
			}

			self.isInTransaction = false;
			self.onBitsUsed = onBitsUsed;

			// If the transaction id exists in the array, it means EventSub fired before the
			// Twitch helper fired its callback.
			if (!self.transactionIds.includes(transactionObject.transactionId)) {
				//console.log(`Storing transaction id ${transactionObject.transactionId} as processed by JS callback.`);
				self.closeWebSocketConnection();

				self.transactionIds.push(transactionObject.transactionId);
				payload.transactionData = transactionObject;

				self.sendStickerShowRequest(payload);
			} else {
				//console.log(`Transaction id ${transactionObject.transactionId} has already been handled by EventSub.`);
			}
		});

		window.Twitch.ext.bits.onTransactionCancelled(function () {
			//console.log(`Bits transaction cancelled.`);
			self.isInTransaction = false;
			self.closeWebSocketConnection();
		});

		// Check for anonymous user.
		if (!self.store.state.userId) {
			//console.warn('Ignoring sticker for anonymous user.');
			return;
		}

		// Checks to determine if we need to invoke a bits transaction.
		if (self.store.state.isPartyModeOn) {
			self.sendStickerShowRequest(payload);
		} else if (isBroadcaster) {
			self.sendStickerShowRequest(payload);
		} else if (s.bits == 0) {
			self.sendStickerShowRequest(payload);
		} else {
			if (self.store.state.bitsCreditAmount >= s.bits) {
				self.store.commit('setBitsCreditAmount', self.store.state.bitsCreditAmount - s.bits);
				self.sendStickerShowRequest(payload);
			} else {
				logEvent('bits_transaction_start', payload);
				self.isInTransaction = true;
				self.openWebSocketConnection();
				window.Twitch.ext.bits.useBits(s.sku);
			}
		}
	}

	// Send a request to the server to show the sticker.
	sendStickerShowRequest(payload) {
		// For global stickers, we need to check the streamer's configured size and change the ID.
		if (payload.stickerType == StickerType.GLOBAL) {
			const id = payload.stickerId.substring(payload.stickerId.lastIndexOf('_') + 1, payload.stickerId.length);
			const gss = this.store.state.globalStickerSize;

			if (gss == GlobalStickerSize.SMALL) {
				payload.stickerId = 'sticker_small_' + id;
			} else if (gss == GlobalStickerSize.MEDIUM) {
				payload.stickerId = 'sticker_medium_' + id;
			} else if (gss == GlobalStickerSize.LARGE) {
				payload.stickerId = 'sticker_large_' + id;
			}
		}

		axios
			.post('/show-sticker', payload)
			.then(res => {
				if (res.status === 200) {
					this.store.commit('setExtensionOnline', true);
					this.onComplete();
					if (this.onBitsUsed) {
						this.onBitsUsed();
					}
				} else {
					this.store.commit('setExtensionOnline', false);
				}
			})
			.catch(error => {
				this.store.commit('setExtensionOnline', false);

				// Show the error dialog only if a TTS synthesis error occurred.
				const data = error.response.data;
				if (data.ttsError) {
					showGlobalError(data.error);
				}
			});
	}

	openWebSocketConnection() {
		const self = this;

		if (self.ws) {
			self.closeWebSocketConnection();
		}

		// TODO pass auth token
		self.ws = new WebSocket(getWssEndpoint() + `/use-bits/${this.store.state.twitchAuthToken}`);

		// Close the socket after a minute as a fallback.
		setTimeout(() => {
			self.closeWebSocketConnection();
		}, 60000);

		self.ws.onmessage = function (event) {
			const payload = JSON.parse(event.data);

			// If the JS callback did not execute, we will not have a matching transaction
			// id in the array. So, the EventSub backup is handled here.
			if (!self.transactionIds.includes(payload.transaction.id)) {
				//console.log(`Storing transaction id ${payload.transaction.id} as processed by EventSub.`);
				self.transactionIds.push(payload.transaction.id);
				self.onBitsUsed();
				self.onComplete();
			} else {
				//console.log(`Transaction id ${payload.transaction.id} has already been handled by JS callback.`);
			}

			self.closeWebSocketConnection();
		};

		self.ws.onopen = () => {
			//console.log('Opened extension web socket connection.');
		};

		self.ws.onclose = () => {
			//console.log('Closed extension web socket connection.');
		};

		self.ws.onerror = function (event) {
			console.error('WS error occurred');
			console.error(JSON.stringify(event, null, 4));
			self.closeWebSocketConnection();
		};
	}

	closeWebSocketConnection() {
		if (this.ws && this.ws.readyState == 1) {
			this.ws.close();
		}
		this.ws = undefined;
	}
}

export { StickersTabShowSticker };
